
import Modal from "@/components/Modal.vue";
import MultiSelect from "@/components/MultiSelect.vue";
import { apiCall } from "@/lib/api";
import { gettersInstance, logError, getApiaTooltip, getCropLabelWithApia } from "@/lib/util";
import CustomDateInput from "@/components/CustomDateInput.vue";
import { formatDate } from "../../lib/util";
import { Component, MixedVue, mixins } from "@/vue";
import { Prop, Ref } from "vue-property-decorator";
import { Parcel } from "@/lib/types/Parcel";
import { Crop } from "@/lib/types/Crop";
import { CropDatesEstimate } from "@/lib/types/actions/CropDatesEstimate";

const StoreGetters = gettersInstance(["allCropGroups", "currentFarmId", "currentSeasonId", "currentSeason", "parcelIndex"]);

@Component({
  name: "SetCropsModal",
  components: { MultiSelect, Modal, CustomDateInput },
})
export default class SetCropsModal extends mixins(MixedVue, StoreGetters) {
  static components: {
    Modal: typeof Modal;
    MultiSelect: typeof MultiSelect;
    CustomDateInput: typeof CustomDateInput;
  };

  $emit: {
    (e: "close"): any;
    (e: "change", changedParcels: Parcel[]): any;
  };

  @Ref()
  modal: Modal;

  $props: {
    parcelIds: Parcel["id"][];
  };

  @Prop({ type: Array, required: true })
  parcelIds: Parcel["id"][];

  selectedCrop: Crop = null;
  sowDate: string = null;
  harvestDate: string = null;
  estimatedSowDate: string = null;
  estimatedHarvestDate: string = null;

  get crops() {
    const result = Object.values(this.allCropGroups).map((crop) => {
      const apiaLink = crop.system_links?.find((link) => link.name === "APIA");
      const apiaCode = apiaLink?.crop_code || null;
      return {
        id: [...crop.cropIds][0],
        name: crop.name[this.$language.current],
        // Make a separate group for the unspecified crop
        group: crop.id == "null" ? this.$gettext("Unspecified crop") : "",
        system_links: crop.system_links || [],
        apia_code: apiaCode,
        title: getApiaTooltip(apiaCode),
      };
    });
    result.slice(0, 5).forEach((crop) => {
      crop.group = this.$gettext("Frequently used");
    });
    // Take the noCropObject from the end of the array and put it at the beginning
    let noCropObject = result.pop();
    result.splice(0, 0, noCropObject);
    return result;
  }
  getCropLabelWithApia = getCropLabelWithApia;
  get minSowDate() {
    return formatDate(this.currentSeason.start_date);
  }
  get maxSowDate() {
    const seasonEndDate = formatDate(this.currentSeason.end_date);
    return this.getDateMinMax(seasonEndDate, this.harvestDate, false);
  }
  get minHarvestDate() {
    const seasonStartDate = formatDate(this.currentSeason.start_date);
    return this.getDateMinMax(seasonStartDate, this.sowDate, false);
  }
  get maxHarvestDate() {
    const seasonEndDate = formatDate(this.currentSeason.end_date);
    return this.getDateMinMax(seasonEndDate, this.sowDate, false);
  }
  get showDates() {
    return this.selectedCrop && this.selectedCrop.id !== "null";
  }
  get formValid() {
    return !!this.selectedCrop;
  }

  async onCropChange(crop: Crop) {
    const parcelId = this.parcelIds[0];
    if (crop.id == "null") {
      this.estimatedSowDate = null;
      this.estimatedHarvestDate = null;
      this.sowDate = null;
      this.harvestDate = null;
      return;
    }
    const apiaLink = crop.system_links?.find((link) => link?.name === "APIA")?.name?.toLowerCase();

    if (!apiaLink) return;
    const estimatedData = await apiCall<CropDatesEstimate>(
      "GET",
      `/farm/${this.currentFarmId}/season/${this.currentSeasonId}/parcel/${parcelId}/crop_dates_estimate/${apiaLink}/${crop.id}/`
    );
    this.estimatedSowDate = estimatedData.start_date;
    this.estimatedHarvestDate = estimatedData.end_date;
    this.sowDate = null;
    this.harvestDate = null;
  }

  getDatePlaceholder(user: string, estimated: string) {
    const date = user || estimated;
    if (!date) return this.$gettext("Select a date...");
    let str = new Date(date).toLocaleDateString(this.$language.current, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    if (!user) str += "*";
    return str;
  }

  /**
   * The following logic applies:
   *  - We ask the user if they want to skip parcels that already have 1 crop interval
   *  - We skip parcels that have 2 or more crop intervals
   */
  async setCrops() {
    let parcels = this.parcelIds.map((id) => this.parcelIndex[id]);
    const parcelsWithCropSet = parcels.filter((parcel) => parcel.crop_intervals.length == 1);
    if (parcelsWithCropSet.length > 0) {
      if (
        !confirm(
          this.$gettextInterpolate(this.$gettext("%{ nr } out of %{ total } parcels already have the crop set. Do you want to replace it?"), {
            nr: parcelsWithCropSet.length,
            total: parcels.length,
          })
        )
      ) {
        parcels = parcels.filter((parcel) => parcel.crop_intervals.length != 1);
      }
    }
    parcels = parcels.filter((parcel) => parcel.crop_intervals.length < 2); // skip parcels with 2 or more crops
    const parcelIds = parcels.map((parcel) => parcel.id);
    try {
      await apiCall(
        "POST",
        `/farm/${this.currentFarmId}/season/${this.currentSeasonId}/replace_parcels_interval/`,
        {},
        {
          parcels: parcelIds,
          crop: this.selectedCrop.id == "null" ? null : this.selectedCrop.id,
          interval: {
            lower: this.sowDate || this.estimatedSowDate,
            upper: this.harvestDate || this.estimatedHarvestDate,
          },
        }
      );
      this.notify({
        type: "success",
        icon: "spa",
        text: this.$gettextInterpolate(this.$ngettext("Crop set for %{ n } parcel", "Crop set for %{ n } parcels", parcelIds.length), { n: parcelIds.length }),
      });
      this.$emit(
        "change",
        parcels.map((parcel) => ({
          ...parcel,
          crop_intervals: [
            {
              crop_id: this.selectedCrop.id,
              crop: this.selectedCrop,
              interval: {
                lower: this.sowDate || this.estimatedSowDate,
                upper: this.harvestDate || this.estimatedHarvestDate,
                bounds: "[)",
              },
            },
          ],
        }))
      );
      this.$store.commit("clearParcelOverlapCache");
      this.modal.closeModal();
    } catch (e) {
      logError(e);
      this.notify({
        type: "error",
        text: this.$gettext("Unable to set parcel crops"),
      });
    }
  }

  getDateMinMax(date1: string, date2: string, min = true) {
    if (!date1 || !date2) return date1 || date2;
    const sorted = [date1, date2].sort();
    return min ? sorted[0] : sorted[1];
  }
}
